<template>
  <el-table
    ref="table"
    v-loading="camerasListLoading"
    class="list"
    :data="cameras"
    :max-height="tableHeight"
    :row-class-name="tableRowClassName"
    @sort-change="sortChange"
    @filter-change="filterChange"
    v-on="$listeners"
  >
    <el-table-column v-if="$can('update', 'cameras')" type="selection" :selectable="selectable" width="55" />
    <el-table-column prop="short_name" label="Название" sortable="custom" >
      <template slot-scope="scope">
        <router-link class="link" :to="{ name: 'cameras:edit', params: { id: scope.row.id_camera } }">
          {{ scope.row.short_name }}
        </router-link>
      </template>
    </el-table-column>
    <el-table-column prop="owner_client_name" label="Владелец" sortable="custom" />
    <el-table-column prop="nimble_id" label="Camera ID" sortable="custom" />
    <el-table-column prop="serial_number" label="Серийный номер" sortable="custom" />
    <el-table-column prop="ip" label="IP" sortable="custom" />
    <el-table-column prop="address" label="Адрес" sortable="custom" />
    <el-table-column prop="groups" label="Группа" sortable="custom">
      <template slot-scope="scope">
        <span class="groups__list" :title="scope.row.groups.join(' / ')">{{ scope.row.groups.join(' / ') }}</span>
      </template>
    </el-table-column>
    <el-table-column
      v-if="$can('read', 'cameras_archive_depth')"
      prop="archive_depth"
      label="Квота хранения"
      sortable="custom"
    >
      <template slot-scope="scope">
        {{ scope.row.archive_depth }} {{ declOfNum(scope.row.archive_depth, ["день", "дня", "дней"]) }}
      </template>
    </el-table-column>
    <el-table-column prop="priority" label="Приоритет" sortable="custom" />
    <el-table-column v-if="$can('read', 'cameras_dvr_server')" prop="server_name" label="DVR-сервер" sortable="custom" />
    <el-table-column prop="proxy_server_name" label="Proxy-сервер" sortable="custom" />
    <el-table-column
      v-if="$can('read', 'cameras_state')"
      prop="camera_status"
      label="Статус"
      width="100"
      align="center"
      column-key="camera_status_column"
      :filters="statusFilters"
      :filtered-value="statusFilter ? [statusFilter] : []"
      :filter-multiple="false"
    >
      <template slot-scope="scope">
        <el-tooltip
          v-if="scope.row.state === 'deleted'"
          effect="dark"
          placement="bottom-end"
          :content="statuses[scope.row.state].desc"
        >
          <i :class="statuses[scope.row.state].icon" />
        </el-tooltip>
        <el-tooltip
          v-else-if="statuses[scope.row.camera_status]"
          effect="dark"
          placement="bottom-end"
          :content="statuses[scope.row.camera_status].desc"
        >
          <i :class="statuses[scope.row.camera_status].icon" />
        </el-tooltip>
      </template>
    </el-table-column>
  </el-table>
</template>

<script>
import { declOfNum } from "@/core/helpers/parsers";
import statuses from "@/components/Cameras/cameraStatuses";

const STATUS_FILTERS_DEFAULT = [
  { text: 'Камера включена', value: 'online' },
  { text: 'Камера отключена', value: 'offline' },
  { text: 'Проблема с потоком', value: 'error' },
  { text: 'В спящем режиме', value: 'sleep' },
]

export default {
  name: "CamerasTable",
  props: {
    cameras: {
      type: Array,
      default: () => [],
    },
    camerasListLoading: {
      type: Boolean,
      default: false,
    },
    query: {
      type: String,
      default: ""
    },
    statusFilter: {
      type: String,
      default: "",
    },
    showDeletedCameras: {
      type: Boolean,
      required: true,
    }
  },
  data() {
    return {
      statuses,
      statusFilters: [...STATUS_FILTERS_DEFAULT],
      tableHeight: 500,
      restElementsHeight: 348,
    };
  },
  beforeMount() {
    this.calculateTableHeight()
  },
  mounted() {
    window.addEventListener('resize', this.calculateTableHeight)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateTableHeight)
  },
  methods: {
    declOfNum,
    calculateTableHeight() {
      this.tableHeight = window.innerHeight - this.restElementsHeight
    },
    toggleCamerasSelection(cameras) {
      this.customCamerasList
        .filter((camera) => cameras.includes(camera.id_camera))
        .forEach((camera) => {
          this.$refs.table.toggleRowSelection(camera);
        });
    },
    sortChange(event) {
      this.$emit("sort", event.prop, event.order);
    },
    filterChange(filters) {
      const filterValues = filters.camera_status_column || [];
      this.applyStatusFilter(filterValues.length === 1 ? filterValues[0] : "");
    },
    applyStatusFilter(filterValue) {
      const queryWithoutStatus = this.removeStatusFilter(this.query, this.statusFilter);
      this.$emit("update:status-filter", filterValue);
      const query = [queryWithoutStatus, filterValue].filter(Boolean).join(" ");

      this.$emit("update:query", query);
      this.$emit("search", query);
    },
    removeStatusFilter(query, filterValue) {
      const trimmedQuery = query.trim();
      if (!filterValue || trimmedQuery === "") {
        return trimmedQuery;
      }

      let statusIndex = trimmedQuery.lastIndexOf(filterValue);
      while (statusIndex >= 0) {
        const startsToken = statusIndex === 0 || /\s/.test(trimmedQuery[statusIndex - 1]);
        const statusEnd = statusIndex + filterValue.length;
        const endsToken = statusEnd === trimmedQuery.length || /\s/.test(trimmedQuery[statusEnd]);
        if (startsToken && endsToken) {
          const beforeStatus = trimmedQuery.slice(0, statusIndex).trimEnd();
          const afterStatus = trimmedQuery.slice(statusEnd).trimStart();
          return [beforeStatus, afterStatus].filter(Boolean).join(" ");
        }
        statusIndex = trimmedQuery.lastIndexOf(filterValue, statusIndex - 1);
      }

      return trimmedQuery;
    },
    updateDeletedMode(showDeletedCameras) {
      this.statusFilters = showDeletedCameras
        ? [...STATUS_FILTERS_DEFAULT, { text: 'Камера удалена', value: 'deleted' }]
        : [...STATUS_FILTERS_DEFAULT];

      if (!showDeletedCameras && this.statusFilter === "deleted") {
        this.applyStatusFilter("");
        this.$refs.table.clearFilter(["camera_status_column"]);
        return true;
      }

      return false;
    },
    clearStatusFilter() {
      this.$emit("update:status-filter", "");
      this.$refs.table.clearFilter(["camera_status_column"]);
    },
    tableRowClassName({row}) {
      return row.state === 'deleted' ? 'deleted-row' : null;
    },
    selectable(row) {
      return row.state !== 'deleted'
    },
  },
};
</script>

<style lang="scss">
.groups__list {
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
  display: -webkit-box;
  text-overflow: ellipsis;
}
.deleted-row td {
  opacity: 0.3;
  &:last-child {
    opacity: 1;
    .el-icon-error {
      color: black;
    }
  }
}
</style>
